import type { ContentIngestionProposedGroup } from "@contracts/contentIngestion";

/** Matches server `BulkImageIngestionController.NormalizeRelativePath` for analyze manifests. */
export function normalizeIngestionRelativePath(rawPath: string): string {
  return rawPath
    .replace(/\\/g, "/")
    .split("/")
    .filter(Boolean)
    .join("/")
    .trim();
}

/** Matches server `NormalizeFolderPrefix`. */
export function normalizeIngestionFolderPrefix(rawPrefix: string): string {
  return normalizeIngestionRelativePath(rawPrefix).replace(/\/+$/, "");
}

/** Matches server `PathMatchesFolderPrefix` (ordinal case-insensitive). */
export function pathMatchesIngestionFolderPrefix(relativePath: string, folderPrefix: string): boolean {
  if (!relativePath?.trim() || !folderPrefix?.trim()) {
    return false;
  }
  const p = relativePath;
  const prefix = folderPrefix;
  if (p.localeCompare(prefix, undefined, { sensitivity: "base" }) === 0) {
    return true;
  }
  return p.toLowerCase().startsWith(`${prefix.toLowerCase()}/`);
}

/**
 * Matches paths whose full relative path starts with `filenamePrefix` and then a non-alphanumeric boundary
 * (e.g. `.../BigOleCentralPark` matches `.../BigOleCentralPark-14.jpg`), so `Park` does not match `Parking`.
 */
export function pathMatchesIngestionFilenamePrefix(relativePath: string, filenamePrefix: string): boolean {
  if (!relativePath?.trim() || !filenamePrefix?.trim()) {
    return false;
  }
  const p = relativePath.toLowerCase();
  const prefix = filenamePrefix.toLowerCase();
  if (!p.startsWith(prefix) || p.length === prefix.length) {
    return false;
  }
  return !/[a-z0-9]/.test(p[prefix.length]);
}

export const ATLAS_INGESTION_FALLBACK_GROUP_NAME = "Ungrouped images";
export const ATLAS_INGESTION_FALLBACK_GROUP_RATIONALE =
  "Remaining files that were not covered by explicit file or folder-prefix mappings.";

/**
 * Best-effort backfill for groups that lack AI-provided folder prefixes (notably unmatched/excluded buckets).
 * Returns unique parent folders derived from file paths; empty when all files are root-level.
 */
export function backfillFolderPrefixesFromFilePaths(filePaths: Array<string>): Array<string> {
  const parentFolders = new Set<string>();
  for (const rawPath of filePaths) {
    const normalized = normalizeIngestionRelativePath(rawPath);
    const slashIndex = normalized.lastIndexOf("/");
    if (slashIndex > 0) {
      parentFolders.add(normalized.slice(0, slashIndex));
    }
  }
  return [...parentFolders].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

export interface ResolvedAtlasIngestionGroups {
  matchedGroups: Array<ContentIngestionProposedGroup>;
  unmatchedGroup: ContentIngestionProposedGroup | null;
}

/**
 * Resolves which accepted files belong to each proposed group: explicit `filePaths` first (first group wins),
 * then `folderPrefixes`, then `filenamePrefixes` (first group wins), then optional client fallback for unmatched paths.
 * Unmatched paths are returned separately (not as a normal upload group).
 */
export function resolveAtlasIngestionGroups(
  proposedGroups: Array<ContentIngestionProposedGroup>,
  acceptedRelativePaths: Array<string>
): ResolvedAtlasIngestionGroups {
  const acceptedPaths = [
    ...new Set(acceptedRelativePaths.map(p => normalizeIngestionRelativePath(p)).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  const acceptedSet = new Set(acceptedPaths);

  const assignments = new Map<string, number>();

  // Pass 1 — explicit file paths per group in order
  for (let gi = 0; gi < proposedGroups.length; gi++) {
    const explicit = (proposedGroups[gi].filePaths ?? []).map(p => normalizeIngestionRelativePath(p)).filter(Boolean);
    for (const p of explicit) {
      if (!acceptedSet.has(p)) {
        continue;
      }
      if (assignments.has(p)) {
        continue;
      }
      assignments.set(p, gi);
    }
  }

  // Pass 2 — folder prefixes per group in order
  for (let gi = 0; gi < proposedGroups.length; gi++) {
    const prefixes = (proposedGroups[gi].folderPrefixes ?? []).map(normalizeIngestionFolderPrefix).filter(Boolean);
    for (const prefix of prefixes) {
      for (const p of acceptedPaths) {
        if (assignments.has(p)) {
          continue;
        }
        if (pathMatchesIngestionFolderPrefix(p, prefix)) {
          assignments.set(p, gi);
        }
      }
    }
  }

  // Pass 2.5 — filename stem prefixes (folder path + shared name portion, not a real folder)
  for (let gi = 0; gi < proposedGroups.length; gi++) {
    const stems = (proposedGroups[gi].filenamePrefixes ?? []).map(normalizeIngestionFolderPrefix).filter(Boolean);
    for (const stem of stems) {
      for (const p of acceptedPaths) {
        if (assignments.has(p)) {
          continue;
        }
        if (pathMatchesIngestionFilenamePrefix(p, stem)) {
          assignments.set(p, gi);
        }
      }
    }
  }

  const result: Array<ContentIngestionProposedGroup> = proposedGroups.map((g, gi) => ({
    ...g,
    folderPrefixes: [...(g.folderPrefixes ?? [])],
    filenamePrefixes: [...(g.filenamePrefixes ?? [])],
    filePaths: acceptedPaths.filter(p => assignments.get(p) === gi).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
  }));

  const unassigned = acceptedPaths.filter(p => !assignments.has(p));
  const matchedGroups = result;
  const unmatchedGroup: ContentIngestionProposedGroup | null =
    unassigned.length === 0
      ? null
      : {
          groupId: crypto.randomUUID(),
          name: ATLAS_INGESTION_FALLBACK_GROUP_NAME,
          rationale: ATLAS_INGESTION_FALLBACK_GROUP_RATIONALE,
          filePaths: unassigned,
          folderPrefixes: [],
          filenamePrefixes: [],
        };

  return { matchedGroups, unmatchedGroup };
}
